/**
 * Checkout context: stop offering "pay" to a party who has already paid.
 *
 * WHAT WAS WRONG. renderCheckout paints its pay control unconditionally
 * (app.js:3229):
 *
 *     <button class="btn-offer co-cta" data-completecheckout="${t.id}">
 *       Complete Checkout · ${money(total)}</button>
 *
 * A trade has two payers, and the server has always known which of them is
 * done — `myCheckoutComplete` has been on the trade-checkout-summary response
 * this whole time (offers.ts:2191), top-level and unconditional. Nothing on the
 * web read it. So a seller who had already completed checkout came back to the
 * screen and was offered "Complete Checkout · $265" again, naming a figure they
 * did not owe.
 *
 * 🪤 THE CLICK BEING DEAD IS NOT THE SAME AS THE OFFER BEING ABSENT.
 * [data-completecheckout] is GUARDED_WRITES[0] (writeGuard.ts:98), so the click
 * already does nothing. That fixes the WRITE and leaves the STATEMENT, and the
 * statement is the part that is false: a button reading "Complete Checkout ·
 * $265" tells someone they owe $265. Deadening a lie does not retire it.
 *
 * WHY HIDE RATHER THAN RELABEL. Replacing the control with a "you have already
 * paid" treatment needs a sentence, and user-facing wording in this layer is a
 * product decision, not ours — writeGuard.ts:604-614 rules exactly that, and
 * Blake has an open question on the post-checkout copy. Removing a false offer
 * needs no new words; making a true statement in its place does. So this hides
 * the control and leaves his `co-fine` line ("Nothing releases to @X until you
 * confirm delivery", app.js:3230) standing, because that one is true either way.
 *
 * WHY AN OBSERVER AND NOT A CLICK HANDLER. Every other guard in this layer
 * intercepts a click, because a click is when the damage happens. Here the
 * damage is already done by the time anything is clicked — it is on screen. So
 * this watches for the paint, the same posture installDealThreadLoader uses for
 * its own sweep.
 *
 * THE TWO STATES THAT ARE NOT THE SAME. __gbzCheckoutContext returns null when
 * no context has landed for that row — a fetch still in flight, or a deal whose
 * summary was never requested. That is NOT KNOWN, and it must leave the control
 * exactly as Blake painted it. Only an explicit `myCheckoutComplete === true`
 * hides anything. Getting this backwards would hide the pay control from
 * someone who has not paid, which blocks a real payment on a live money screen
 * — strictly worse than the defect being fixed.
 */

import { applyTradeCheckoutSummary } from "./floorTransport";
import type { ApiTradeCheckoutSummary, DemoCheckoutContext } from "./floorTransport";
import { LONG_TOAST_MS } from "./wantsWrite";

/** Blake's pay control. Also GUARDED_WRITES[0]; see the docblock. */
export const PAY_CTA_SELECTOR = "[data-completecheckout]";

/** Marks a control this module hid, so a re-sweep is idempotent and a test can
 *  tell "hidden by us" from "never rendered". */
export const SUPPRESSED_ATTR = "data-gbz-checkout-complete";

type ContextReader = (threadId: string) => DemoCheckoutContext | null;

/**
 * Hides the pay control on any deal whose context says this party is done.
 *
 * Returns a teardown, per the house convention.
 */
export function installCheckoutContext(win: Window): () => void {
  const readContext = (threadId: string): DemoCheckoutContext | null => {
    const reader = (win as unknown as { __gbzCheckoutContext?: ContextReader })
      .__gbzCheckoutContext;
    if (typeof reader !== "function") return null;
    try {
      return reader(threadId);
    } catch {
      /* The reader closes over THREADS, a data.js binding. A call that somehow
         arrived before that script evaluated would throw rather than return
         undefined, inside an observer callback. Same posture as the counter
         readers in the bridge. */
      return null;
    }
  };

  const sweep = (): void => {
    const controls = win.document.querySelectorAll<HTMLElement>(PAY_CTA_SELECTOR);
    for (const el of controls) {
      const threadId = el.dataset.completecheckout;
      if (!threadId) continue;
      const ctx = readContext(threadId);
      // null is "not known" and must not hide anything. See the docblock.
      if (ctx?.myCheckoutComplete !== true) continue;
      if (el.hasAttribute(SUPPRESSED_ATTR)) continue;
      el.setAttribute(SUPPRESSED_ATTR, "1");
      el.hidden = true;
    }
  };

  // Immediately, not only on the next mutation: the checkout screen can already
  // be painted when this installs (a deep link, or a late install after boot).
  sweep();

  const app = win.document.getElementById("app");
  const observer = app ? new MutationObserver(() => sweep()) : null;
  observer?.observe(app!, { childList: true, subtree: true });

  return () => observer?.disconnect();
}

/* ── S3: FEES ON ROUTE ENTRY ─────────────────────────────────────────────────
   A refresh on #/checkout/<id> dumped the buyer on the home feed. route()
   resolves that hash only for a thread that already HAS fees in memory:

       const t = THREADS.find((x) => x.id === m[1] && x.fees);   (app.js:9067)

   and fees arrive only when someone taps "Tap for summary" on an accepted deal.
   A fresh page load has the deals list (installDeals) but never ran that tap,
   so the route fell through to `{ view: "home" }` mid-payment, with no
   explanation. It is also where a 3DS redirect lands, so without this every
   card that needs SCA would come back to the feed.

   WHAT THIS DOES. On entry (a deep link, after the deals list installs, or a
   hashchange) to a checkout hash whose row has no fees: ask the same
   trade-checkout-summary the toggle asks, install it through the same path
   (applyTradeCheckoutSummary), and let the bridge repaint. route() then
   resolves to checkout. It never enables paying: [data-completecheckout] stays
   in GUARDED_WRITES and checkoutCtaGuard is untouched.

   🔴 THE 422 AND THE 502 ARE NOT SYMMETRIC, and a branch keyed on `body.code`
   silently misses every 502. The 422 carries { error, code:
   "shipping_address_required" } and `error` is an authored sentence
   (offers.ts:2096). The 502 carries ONLY { error } (offers.ts:2132), and
   `error` there is err.message, which on one of its three paths is a raw
   vendor or network string. So `error` is surfaced verbatim for the 422's code
   alone, and for a 502 the endpoint's own default sentence stands in. Neither
   is copy of ours: every word below is one the server already wrote.

   Any other refusal (403, 404, a network error, an offer that is no longer
   accepted) has no sentence we are allowed to show, and user-facing wording is
   a product decision (writeGuard.ts:604-614). Those log and leave the visitor
   where route() puts them, which is today's behaviour, not a new one. */

/** His checkout route, as route() matches it. */
export const CHECKOUT_HASH = /^#\/checkout\/(.+)$/;

/** The one 422 code whose `error` is an authored sentence (offers.ts:2096). */
export const SHIPPING_ADDRESS_REQUIRED = "shipping_address_required";

/** The 502's own default sentence (offers.ts:2132), used INSTEAD of its `error`. */
export const QUOTE_UNAVAILABLE_MESSAGE = "Could not retrieve a live shipping rate. Please try again.";

/** The sentence a refused summary may show the buyer, or null for none. */
export function checkoutRefusalMessage(status: number, body: unknown): string | null {
  const b = (body ?? {}) as { code?: unknown; error?: unknown };
  const hasError = typeof b.error === "string" && b.error !== "";
  if (b.code === SHIPPING_ADDRESS_REQUIRED) return hasError ? (b.error as string) : null;
  /* Requires the endpoint's own { error } shape: a gateway's 502 page is not a
     shipping-rate failure and must not be reported as one. */
  if (status === 502 && hasError) return QUOTE_UNAVAILABLE_MESSAGE;
  return null;
}

interface CheckoutEntryWindow extends Window {
  __gbzCheckoutEntry?: (threadId: string) => { hasFees: boolean; offerId: string | null } | null;
  toast?: (message: string, ms?: number) => void;
}

export function installCheckoutRouteEntry(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  notify: (message: string, ms?: number) => void = (message, ms) => {
    // #toast only exists after app.js's first render; see dealActions.
    if (!win.document.getElementById("toast")) return;
    (win as CheckoutEntryWindow).toast?.(message, ms);
  },
): () => void {
  const inFlight = new Set<string>();

  /* 🔴 THE SENTENCE MUST OUTLIVE THE BOOT'S REPAINTS. This entry fetch starts
     before WebInstall's remaining awaits (wants, notifications, installAccount),
     and installAccount repaints whenever the hash starts with #/checkout
     (__gbzInstallAccount), including when the refusal left the buyer on home. Every
     render() emits a FRESH EMPTY toast node (app.js:7969), so a sentence shown in
     the meantime is wiped, and the buyer gets the feed with no reason: the bounce
     S3 exists to explain. The 422 is a plain read and answers well inside that
     window; the 502 waits on a rate quote and usually lands after it.

     So the sentence is shown again where a repaint took it, the same posture as
     wantsWrite's retoast: only while its toast's life has not run out, only while
     the visitor is still on that checkout, only into a toast node that is NEW and
     empty, so a message written after the repaint is never covered, and only if
     no newer toast had already replaced it before the repaint.

     It is the buyer's only explanation for landing on the feed, so it stays up for
     LONG_TOAST_MS (the length this site gives a sentence about what did NOT
     happen), not data.js's default 2.6 s. */
  let shownMessage: { message: string; threadId: string; at: number; node: HTMLElement } | null = null;
  const show = (message: string, threadId: string, at: number): void => {
    notify(message, LONG_TOAST_MS);
    const node = win.document.getElementById("toast");
    shownMessage = node ? { message, threadId, at, node } : null;
  };
  const reshow = (): void => {
    const s = shownMessage;
    if (!s) return;
    if (
      Date.now() - s.at > LONG_TOAST_MS ||
      win.location.hash !== `#/checkout/${s.threadId}` ||
      s.node.textContent !== s.message
    ) {
      shownMessage = null;
      return;
    }
    const node = win.document.getElementById("toast");
    if (node && node !== s.node && !node.textContent) show(s.message, s.threadId, s.at);
  };
  const app = win.document.getElementById("app");
  const observer = app ? new MutationObserver(reshow) : null;
  observer?.observe(app!, { childList: true, subtree: true });

  const load = async (threadId: string, offerId: string): Promise<void> => {
    const res = await authFetch(`/api/offers/${encodeURIComponent(offerId)}/trade-checkout-summary`);
    if (!res.ok) {
      const message = checkoutRefusalMessage(res.status, await res.json().catch(() => null));
      console.warn(`[web-install] checkout: summary for ${threadId} refused (${res.status})`);
      // Not if the visitor has moved on while the quote was being fetched.
      if (message && win.location.hash === `#/checkout/${threadId}`) show(message, threadId, Date.now());
      return;
    }
    applyTradeCheckoutSummary(win, threadId, (await res.json()) as ApiTradeCheckoutSummary);
  };

  const enter = (): void => {
    const m = CHECKOUT_HASH.exec(win.location.hash);
    if (!m) return;
    const threadId = m[1];
    // null row = the deals list has not installed (or a guest): nothing to ask.
    const entry = (win as CheckoutEntryWindow).__gbzCheckoutEntry?.(threadId);
    if (!entry || entry.hasFees || !entry.offerId || inFlight.has(threadId)) return;
    inFlight.add(threadId);
    load(threadId, entry.offerId)
      .catch((err) => console.warn(`[web-install] checkout: summary for ${threadId} not installed:`, err))
      .finally(() => inFlight.delete(threadId));
  };

  // Immediately as well as on hashchange: a refresh sets the hash before
  // anything of ours runs, so no hashchange ever fires for it.
  enter();
  win.addEventListener("hashchange", enter);
  return () => {
    win.removeEventListener("hashchange", enter);
    observer?.disconnect();
  };
}
